import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, ChevronRightIcon } from 'lucide-react';
interface NavSection {
  id: string;
  label: string;
  group: string;
}
const sections: NavSection[] = [
// General Information
{
  id: 'overview',
  label: 'Overview',
  group: 'General Information'
}, {
  id: 'guest-journey',
  label: 'Guest Journey',
  group: 'General Information'
}, {
  id: 'story-arc',
  label: 'Story Arc',
  group: 'General Information'
},
// Production Teams
{
  id: 'team-1',
  label: '1. Secret Location',
  group: 'Production Teams'
}, {
  id: 'team-2',
  label: '2. AThink Studio',
  group: 'Production Teams'
}, {
  id: 'team-3',
  label: '3. Scent & Drink',
  group: 'Production Teams'
}, {
  id: 'team-4',
  label: '4. Food Team',
  group: 'Production Teams'
}, {
  id: 'team-5',
  label: '5. Sales Team',
  group: 'Production Teams'
}, {
  id: 'team-6',
  label: '6. History Team',
  group: 'Production Teams'
}, {
  id: 'team-7',
  label: '7. PR & Marketing',
  group: 'Production Teams'
}, {
  id: 'team-8',
  label: '8. Finance Team',
  group: 'Production Teams'
},
// Resources
{
  id: 'downloads',
  label: 'Downloads',
  group: 'Resources'
}];
const groups = ['General Information', 'Production Teams', 'Resources'];
export function BlueprintNav() {
  const [activeSection, setActiveSection] = useState('overview');
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      const visible = entries.filter(e => e.isIntersecting);
      if (visible.length > 0) {
        const sorted = visible.sort((a, b) => {
          const aRect = a.boundingClientRect;
          const bRect = b.boundingClientRect;
          return Math.abs(aRect.top) - Math.abs(bRect.top);
        });
        setActiveSection(sorted[0].target.id);
      }
    }, {
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    });
    const sectionEls = sections.map(s => document.getElementById(s.id)).filter(Boolean);
    sectionEls.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    }
    setMobileOpen(false);
  };
  return <>
      {/* Desktop sidebar */}
      <nav className="hidden xl:block fixed left-0 top-1/2 -translate-y-1/2 z-30 w-56 pl-5 pr-3" data-id="element-578">
        <div className="bg-[#0f0f23]/90 backdrop-blur-xl border border-[#d4a017]/10 rounded-lg py-4 px-3 max-h-[80vh] overflow-y-auto scrollbar-thin" data-id="element-579">
          {groups.map(group => <div key={group} className="mb-3" data-id="element-580">
              <p className="text-[#c2a878]/50 text-[10px] tracking-[0.2em] uppercase font-medium px-2 mb-1.5" data-id="element-581">
                {group}
              </p>
              {sections.filter(s => s.group === group).map(section => <button key={section.id} onClick={() => scrollTo(section.id)} className={`w-full text-left px-2 py-1.5 rounded text-xs transition-all duration-300 block ${activeSection === section.id ? 'text-[#d4a017] bg-[#d4a017]/10 font-medium' : 'text-[#f5f0e8]/40 hover:text-[#f5f0e8]/70 hover:bg-white/5'}`} data-id="element-582">
                    {section.label}
                  </button>)}
            </div>)}
        </div>
      </nav>

      {/* Mobile floating button */}
      <button onClick={() => setMobileOpen(true)} className="xl:hidden fixed bottom-6 right-6 z-40 w-12 h-12 bg-[#d4a017] text-black rounded-full flex items-center justify-center shadow-lg shadow-[#d4a017]/20" aria-label="Open section navigation" data-id="element-583">
        <MenuIcon className="w-5 h-5" data-id="element-584" />
      </button>

      {/* Mobile drawer */}
      <AnimatePresence data-id="element-585">
        {mobileOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="xl:hidden fixed inset-0 bg-black/80 backdrop-blur-sm z-50" onClick={() => setMobileOpen(false)} data-id="element-586" />
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="xl:hidden fixed right-0 top-0 bottom-0 w-72 bg-[#0f0f23] border-l border-[#d4a017]/10 z-50 overflow-y-auto" data-id="element-587">
              <div className="flex items-center justify-between p-5 border-b border-white/5" data-id="element-588">
                <p className="text-[#d4a017] text-sm tracking-[0.15em] uppercase font-medium" data-id="element-589">
                  Sections
                </p>
                <button onClick={() => setMobileOpen(false)} className="text-white/40 hover:text-white" data-id="element-590">
                  <XIcon className="w-5 h-5" data-id="element-591" />
                </button>
              </div>
              <div className="p-4" data-id="element-592">
                {groups.map(group => <div key={group} className="mb-4" data-id="element-593">
                    <p className="text-[#c2a878]/50 text-[10px] tracking-[0.2em] uppercase font-medium px-2 mb-2" data-id="element-594">
                      {group}
                    </p>
                    {sections.filter(s => s.group === group).map(section => <button key={section.id} onClick={() => scrollTo(section.id)} className={`w-full text-left px-3 py-2.5 rounded text-sm transition-all duration-200 flex items-center justify-between ${activeSection === section.id ? 'text-[#d4a017] bg-[#d4a017]/10' : 'text-[#f5f0e8]/50 hover:text-[#f5f0e8]/80 hover:bg-white/5'}`} data-id="element-595">
                          {section.label}
                          {activeSection === section.id && <ChevronRightIcon className="w-3 h-3 text-[#d4a017]" data-id="element-596" />}
                        </button>)}
                  </div>)}
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}